// Modal de criar/editar atividade. Re-exportado do entry pra outras páginas
// (NegocioDetalhe, Patrimonio, etc) consumirem via deep-link de origem.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Select, Tag, Modal, Form, Typography, Space,
  App as AntApp, Flex,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import ComentariosThread from './ComentariosThread';
import { STATUS_LABEL, PRIORIDADE_LABEL, TIPOS } from './types';
import type { Atividade } from './types';

const { Text } = Typography;

interface OrigemPreset {
  origem_modulo?: string;
  origem_tipo?: string;
  origem_id?: string;
  origem_label?: string;
  atribuicoes?: { email: string; papel: string }[];
}

interface Props {
  atividade: Atividade | null;
  meuEmail: string;
  origemPreset?: OrigemPreset;
  /** Atividade nova criada pelo calendário: já nasce com a data/hora clicada ('AAAA-MM-DDTHH:MM'). */
  dataPreset?: string;
  onClose: () => void;
  onSalvo: () => void;
}

function ModalAtividadeInner({ atividade, meuEmail, origemPreset, dataPreset, onClose, onSalvo }: Props) {
  const { message, modal } = AntApp.useApp();
  const [form, setForm] = useState({
    titulo: atividade?.titulo || '',
    descricao: atividade?.descricao || '',
    tipo: atividade?.tipo || 'tarefa',
    status: atividade?.status || 'pendente',
    prioridade: atividade?.prioridade || 'media',
    data_vencimento: atividade?.data_vencimento ? atividade.data_vencimento.slice(0, 16) : (dataPreset || ''),
    origem_modulo: atividade?.origem_modulo || origemPreset?.origem_modulo || '',
    origem_tipo: atividade?.origem_tipo || origemPreset?.origem_tipo || '',
    origem_id: atividade?.origem_id || origemPreset?.origem_id || '',
    origem_label: atividade?.origem_label || origemPreset?.origem_label || '',
    atribuicoes: atividade?.atribuicoes || origemPreset?.atribuicoes ||
      [{ email: meuEmail, papel: 'responsavel' }],
  });
  const [salvando, setSalvando] = useState(false);
  const [usuarios, setUsuarios] = useState<{ email: string; nome: string }[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/admin/usuarios`,
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios((Array.isArray(data) ? data : [])
          .filter((u: any) => u.enabled !== false)
          .map((u: any) => ({ email: u.email, nome: u.nome || u.email })));
      } catch { setUsuarios([]); }
    })();
  }, []);

  const set = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  async function salvar() {
    if (!form.titulo.trim()) { message.warning('Título obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const payload = {
        ...form,
        data_vencimento: form.data_vencimento || null,
        origem_modulo: form.origem_modulo || null,
        origem_tipo: form.origem_tipo || null,
        origem_id: form.origem_id || null,
        origem_label: form.origem_label || null,
      };
      if (atividade) {
        await axios.post(`${API_URL}/atividades`,
          { action: 'update', id: atividade.id, ...payload },
          { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/atividades`, payload,
          { headers: { Authorization: token } });
      }
      message.success(atividade ? 'Atividade atualizada.' : 'Atividade criada.');
      onSalvo();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  function excluir() {
    if (!atividade) return;
    modal.confirm({
      title: 'Excluir esta atividade?', okText: 'Excluir', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/atividades`,
            { action: 'delete', id: atividade.id },
            { headers: { Authorization: token } });
          message.success('Atividade excluída.');
          onSalvo();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.erro || e.message));
        }
      },
    });
  }

  function adicionarAtribuicao(email: string) {
    if (!email || form.atribuicoes.some((a: any) => a.email === email)) return;
    set('atribuicoes', [...form.atribuicoes, { email, papel: 'responsavel' }]);
  }
  function removerAtribuicao(email: string) {
    set('atribuicoes', form.atribuicoes.filter((a: any) => a.email !== email));
  }

  return (
    <Modal
      open onCancel={onClose} width={680} destroyOnClose
      title={atividade ? 'Editar atividade' : 'Nova atividade'}
      footer={
        <Flex justify="space-between">
          {atividade
            ? <Button danger icon={<DeleteOutlined />} onClick={excluir}>Excluir</Button>
            : <div />}
          <Space>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" loading={salvando} onClick={salvar}>
              {atividade ? 'Salvar' : 'Criar atividade'}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form layout="vertical" size="small">
        <Form.Item label="Título" required>
          <Input value={form.titulo} autoFocus onChange={e => set('titulo', e.target.value)}
            placeholder="Ex.: Revisar preço da proposta com cliente" />
        </Form.Item>
        <Form.Item label="Descrição">
          <Input.TextArea value={form.descricao} onChange={e => set('descricao', e.target.value)}
            rows={3} placeholder="Detalhes (opcional)" />
        </Form.Item>
        <Flex gap={10}>
          <Form.Item label="Tipo" style={{ flex: 1, marginBottom: 12 }}>
            <Select value={form.tipo} onChange={(v) => set('tipo', v)}
              options={TIPOS.map(t => ({ label: t, value: t }))} />
          </Form.Item>
          <Form.Item label="Status" style={{ flex: 1, marginBottom: 12 }}>
            <Select value={form.status} onChange={(v) => set('status', v)}
              options={Object.entries(STATUS_LABEL).map(([k, v]) => ({ label: v.label, value: k }))} />
          </Form.Item>
          <Form.Item label="Prioridade" style={{ flex: 1, marginBottom: 12 }}>
            <Select value={form.prioridade} onChange={(v) => set('prioridade', v)}
              options={Object.entries(PRIORIDADE_LABEL).map(([k, v]) => ({ label: v.label, value: k }))} />
          </Form.Item>
        </Flex>
        <Form.Item label="Prazo">
          <Input type="datetime-local" value={form.data_vencimento}
            onChange={e => set('data_vencimento', e.target.value)} />
        </Form.Item>
        <Form.Item label="Atribuído a">
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space wrap>
              {form.atribuicoes.map((a: any) => (
                <Tag key={a.email} closable onClose={() => removerAtribuicao(a.email)}
                  color={a.email.toLowerCase() === meuEmail.toLowerCase() ? 'blue' : 'default'}>
                  {a.email}
                </Tag>
              ))}
            </Space>
            <Select value={undefined} onChange={adicionarAtribuicao} placeholder="+ Adicionar usuário..."
              showSearch optionFilterProp="label"
              options={usuarios
                .filter(u => !form.atribuicoes.some((a: any) => a.email === u.email))
                .map(u => ({ label: `${u.nome} (${u.email})`, value: u.email }))} />
          </Space>
        </Form.Item>

        {(form.origem_modulo || atividade?.origem_label) && (
          <Card size="small" style={{ background: '#F0F4FF', marginBottom: 8 }} bodyStyle={{ padding: 8 }}>
            <Text style={{ fontSize: 11, color: '#020D7F' }}>
              📎 Vinculada a: <strong>{form.origem_label || atividade?.origem_label}</strong>
              {atividade?.origem_modulo && <Text type="secondary" style={{ fontSize: 11 }}> ({atividade.origem_modulo})</Text>}
            </Text>
          </Card>
        )}

        {atividade && (
          <Text type="secondary" style={{ fontSize: 10 }}>
            Criada por <strong>{atividade.criado_por}</strong> em{' '}
            {new Date(atividade.criado_em).toLocaleString('pt-BR')}
          </Text>
        )}
      </Form>

      {/* Thread de comentários — só aparece em atividades já criadas
          (precisa do id pra vincular). Suporta menções @email que disparam
          notificação no sino do destinatário. */}
      {atividade?.id && (
        <ComentariosThread
          atividadeId={atividade.id}
          meuEmail={meuEmail}
          usuarios={usuarios}
        />
      )}
    </Modal>
  );
}

// Wrapper que injeta AntApp pra modais/messages funcionarem fora do contexto
// da página principal (importado de NegocioDetalhe, Patrimonio, etc).
export default function ModalAtividade(props: Props) {
  return (
    <AntApp>
      <ModalAtividadeInner {...props} />
    </AntApp>
  );
}
